// Client-side diagnostics buffer for technical errors that the UI hides behind a
// localized generic message.
//
// Background: `humanizeError` deliberately never shows a raw English DB/HTTP
// message in a non-English UI. Until now the raw text was only `console.error`d,
// so a user reporting "تعذّر تنفيذ العملية" left no trace of the real cause and
// the case could not be diagnosed afterwards.
//
// This module keeps the last few technical errors in localStorage:
//   - "lastError"  → a short, human-readable line (already read by the support assistant)
//   - "lastErrors" → a ring buffer (max 10) with reference code, page, time, raw text
//
// Nothing is sent anywhere automatically; it is local context that support can ask for.

const LAST_KEY = "lastError";
const BUFFER_KEY = "lastErrors";
const MAX_ENTRIES = 10;

export type TechnicalErrorRecord = {
  ref: string;
  at: string;
  page: string;
  code: string;
  message: string;
};

/** Short, user-quotable reference code, e.g. "E-9F3KQ2". */
export function newErrorRef(): string {
  const base = Date.now().toString(36) + Math.floor(Math.random() * 36 ** 2).toString(36);
  return `E-${base.slice(-6).toUpperCase()}`;
}

export function recordTechnicalError(params: {
  ref: string;
  code?: string;
  message?: string;
}): void {
  const record: TechnicalErrorRecord = {
    ref: params.ref,
    at: new Date().toISOString(),
    page: typeof window !== "undefined" ? window.location.pathname : "",
    code: params.code || "",
    message: (params.message || "").slice(0, 600),
  };

  try {
    localStorage.setItem(
      LAST_KEY,
      `[${record.ref}] ${record.page} ${record.code ? `(${record.code}) ` : ""}${record.message}`,
    );

    let buffer: TechnicalErrorRecord[] = [];
    try {
      const raw = localStorage.getItem(BUFFER_KEY);
      if (raw) buffer = JSON.parse(raw);
      if (!Array.isArray(buffer)) buffer = [];
    } catch {
      buffer = [];
    }
    buffer.push(record);
    localStorage.setItem(BUFFER_KEY, JSON.stringify(buffer.slice(-MAX_ENTRIES)));
  } catch {
    /* storage unavailable (private mode / quota) — diagnostics are best-effort */
  }
}

export function readTechnicalErrors(): TechnicalErrorRecord[] {
  try {
    const raw = localStorage.getItem(BUFFER_KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}
